import { ReactNode } from "react";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import TooltipInfo from "ds/components/TooltipInfo";

import { getReferencePercentileTooltip } from "../utils";
import { LEGEND_SWATCH_HEIGHT, LEGEND_SWATCH_STROKE_WIDTH, LEGEND_SWATCH_WIDTH } from "./constants";
import styles from "./styles.module.css";

type LegendItem = {
  label: string;
  color: string;
  strokeDasharray?: string;
  fill?: boolean;
  opacity?: number;
  info?: ReactNode;
};

type ConcurrencyChartLegendProps = {
  percentile: number;
  hasContract: boolean;
  hasSpikes: boolean;
};

// Each entry is listed only when the series it names is actually on the plot.
const ConcurrencyChartLegend = ({
  percentile,
  hasContract,
  hasSpikes,
}: ConcurrencyChartLegendProps) => {
  const items: LegendItem[] = [
    {
      label: "Daily max",
      color: "var(--concurrency-chart-range-bar-color)",
      fill: true,
    },
    ...(hasSpikes
      ? [
          {
            label: "Spike",
            color: "var(--concurrency-chart-spike-color)",
            fill: true,
            info: "A day whose true maximum exceeds the chart's scale. The bar is clipped to the top of the axis and labelled with its real peak.",
          },
        ]
      : []),
    {
      label: `P${percentile} to date`,
      color: "var(--concurrency-chart-p95-color)",
      info: hasContract
        ? `The running P${percentile} over the cycle so far (from cycle start). It stabilizes as the cycle progresses; by cycle end it is your billed P${percentile}.`
        : getReferencePercentileTooltip(percentile),
    },
    ...(hasContract
      ? [
          {
            label: "Contract",
            color: "var(--concurrency-chart-contract-color)",
          },
        ]
      : []),
  ];

  return (
    <Box direction="row" wrap align="center" gap="large" fullWidth>
      {items.map((item) => (
        <Box key={item.label} direction="row" align="center" gap="small">
          {item.fill ? (
            <span
              className={styles.legendSwatchFill}
              aria-hidden
              style={{
                background: item.color,
                opacity: item.opacity,
              }}
            />
          ) : (
            <svg
              className={styles.legendSwatchLine}
              width={LEGEND_SWATCH_WIDTH}
              height={LEGEND_SWATCH_HEIGHT}
              aria-hidden
              focusable={false}
            >
              <line
                x1={0}
                y1={LEGEND_SWATCH_HEIGHT / 2}
                x2={LEGEND_SWATCH_WIDTH}
                y2={LEGEND_SWATCH_HEIGHT / 2}
                stroke={item.color}
                strokeWidth={LEGEND_SWATCH_STROKE_WIDTH}
                strokeDasharray={item.strokeDasharray}
                opacity={item.opacity}
              />
            </svg>
          )}
          <Typography variant="p-body3" tag="span" color="secondary">
            {item.label}
          </Typography>
          {item.info !== undefined && <TooltipInfo widthMode="maxWidthSm">{item.info}</TooltipInfo>}
        </Box>
      ))}
    </Box>
  );
};

ConcurrencyChartLegend.displayName = "UsageWorkersNew.ConcurrencyChart.Legend";

export default ConcurrencyChartLegend;
